<div
  class="source-references"
  [class.source-references--dialog]="layout === 'dialog'"
  [attr.aria-busy]="isLoading"
>
  <div class="source-references__toolbar">
    <div class="source-references__search">
      <app-source-references-search
        [source]="source"
        [fullWidth]="layout === 'dialog'"
        [variant]="layout === 'dialog' ? 'toolbar' : 'default'"
        (results)="setResults($event)"
      >
      </app-source-references-search>
    </div>
    <button
      type="button"
      mat-stroked-button
      color="primary"
      class="source-references__add"
      (click)="openModal()"
      [disabled]="isLoading"
    >
      <mat-icon aria-hidden="true">add</mat-icon>
      Add reference
    </button>
  </div>

  <div
    class="source-references__body scrollable-content"
    [class.m-y-24]="layout === 'page'"
  >
    <!-- Shared row actions (table + dialog list) -->
    <ng-template #referenceRowActions let-ref>
      <div class="ref-actions" [class.ref-actions--dialog]="layout === 'dialog'">
        @if (sourceEmbedding) {
          @if (isLinkedToCurrentEmbedding(ref)) {
            <button
              type="button"
              mat-icon-button
              color="primary"
              [disabled]="isLoading"
              (click)="detachReference(ref)"
              matTooltip="Unlink from this embedding"
              aria-label="Unlink reference from this embedding"
            >
              <mat-icon>link_off</mat-icon>
            </button>
          } @else {
            <button
              type="button"
              mat-icon-button
              color="accent"
              [disabled]="isLoading"
              (click)="attachReference(ref)"
              matTooltip="Link to this embedding"
              aria-label="Link reference to this embedding"
            >
              <mat-icon>link</mat-icon>
            </button>
          }
        }
        <button
          type="button"
          mat-icon-button
          [disabled]="isLoading"
          (click)="openModal(ref)"
          matTooltip="Edit reference"
          aria-label="Edit reference"
        >
          <mat-icon>edit</mat-icon>
        </button>
        <button
          type="button"
          mat-icon-button
          color="warn"
          [disabled]="isLoading"
          (click)="deleteReference(ref)"
          matTooltip="Delete reference"
          aria-label="Delete reference"
        >
          <mat-icon>delete_outline</mat-icon>
        </button>
      </div>
    </ng-template>

    <ng-template #referencesTable let-rows>
      @if (rows.length === 0) {
        <div class="ref-empty" role="status">
          <mat-icon class="ref-empty__icon" aria-hidden="true">library_books</mat-icon>
          <p class="ref-empty__title">No references here</p>
          <p class="ref-empty__hint">
            @if (sourceEmbedding) {
              Use search above or add a reference, then link it to this embedding.
            } @else {
              Add a reference or adjust your search.
            }
          </p>
        </div>
      } @else if (layout === 'dialog') {
        <ul class="ref-list-dialog" role="list">
          @for (ref of rows; track trackRef($index, ref); let refIndex = $index) {
            <li class="ref-list-dialog__item" role="listitem">
              <p
                class="ref-list-dialog__text"
                [matTooltip]="ref.referenceText || ''"
                [matTooltipDisabled]="!(ref.referenceText?.length)"
                matTooltipShowDelay="200"
              >
                <span class="ref-reference-label">{{ referenceLabel(ref, refIndex) }}</span>
                {{ ref.referenceText }}
              </p>
              <div class="ref-list-dialog__footer">
                @if (ref.referenceLink) {
                  <a
                    class="ref-list-dialog__link"
                    [href]="ref.referenceLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    [matTooltip]="ref.referenceLink"
                    matTooltipShowDelay="200"
                    (click)="$event.stopPropagation()"
                  >
                    {{ ref.referenceLink }}
                  </a>
                } @else {
                  <span class="ref-list-dialog__no-link">—</span>
                }
                <ng-container *ngTemplateOutlet="referenceRowActions; context: { $implicit: ref }"></ng-container>
              </div>
            </li>
          }
        </ul>
      } @else {
        <div class="references-table-wrap">
          <table mat-table [dataSource]="rows" class="references-table mat-elevation-z0">

            <ng-container matColumnDef="referenceText">
              <th mat-header-cell *matHeaderCellDef scope="col">Reference</th>
              <td mat-cell *matCellDef="let ref; let refIndex = index">
                <span class="ref-cell-text">
                  <span class="ref-reference-label">{{ referenceLabel(ref, refIndex) }}</span>
                  {{ ref.referenceText }}
                </span>
              </td>
            </ng-container>

            <ng-container matColumnDef="referenceLink">
              <th mat-header-cell *matHeaderCellDef scope="col">Link</th>
              <td mat-cell *matCellDef="let ref">
                @if (ref.referenceLink) {
                  <a
                    class="ref-link"
                    [href]="ref.referenceLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    (click)="$event.stopPropagation()"
                  >
                    {{ ref.referenceLink }}
                  </a>
                } @else {
                  <span class="ref-cell-muted">—</span>
                }
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef scope="col" class="mat-column-actions">Actions</th>
              <td mat-cell *matCellDef="let ref" class="mat-column-actions">
                <ng-container *ngTemplateOutlet="referenceRowActions; context: { $implicit: ref }"></ng-container>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="['referenceText', 'referenceLink', 'actions']"></tr>
            <tr mat-row *matRowDef="let row; columns: ['referenceText', 'referenceLink', 'actions'];"></tr>
          </table>
        </div>
      }
    </ng-template>

    @if (sourceEmbedding) {
      <mat-accordion multi class="ref-accordion" [class.ref-accordion--dialog]="layout === 'dialog'">
        <mat-expansion-panel [expanded]="true" class="ref-accordion__panel">
          <mat-expansion-panel-header [collapsedHeight]="'52px'" [expandedHeight]="'52px'">
            <mat-panel-title>Linked to this embedding</mat-panel-title>
            <mat-panel-description>
              <span class="ref-count">{{ linkedReferences.length }}</span>
            </mat-panel-description>
          </mat-expansion-panel-header>
          <ng-container *ngTemplateOutlet="referencesTable; context: { $implicit: linkedReferences }"></ng-container>
        </mat-expansion-panel>
        <mat-expansion-panel [expanded]="true" class="ref-accordion__panel">
          <mat-expansion-panel-header [collapsedHeight]="'52px'" [expandedHeight]="'52px'">
            <mat-panel-title>Not linked to this embedding</mat-panel-title>
            <mat-panel-description>
              <span class="ref-count ref-count--muted">{{ unlinkedReferences.length }}</span>
            </mat-panel-description>
          </mat-expansion-panel-header>
          <ng-container *ngTemplateOutlet="referencesTable; context: { $implicit: unlinkedReferences }"></ng-container>
        </mat-expansion-panel>
      </mat-accordion>
    } @else {
      <ng-container *ngTemplateOutlet="referencesTable; context: { $implicit: source?.externalReferences ?? [] }"></ng-container>
    }
  </div>
</div>
